/**
 * Mapbox GL Draw–based distance measurement for the published tour template.
 * Line strings only; total length sums all completed lines until Clear.
 */
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import length from "@turf/length";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

function formatDistanceMeters(meters) {
  if (!Number.isFinite(meters) || meters < 0) return "—";
  if (meters < 304.8) {
    return `${(meters * 3.28084).toFixed(0)} ft`;
  }
  const mi = meters / 1609.344;
  return `${mi.toFixed(2)} mi`;
}

function totalLineLengthMeters(draw) {
  if (!draw) return 0;
  const fc = draw.getAll();
  let totalKm = 0;
  for (const f of fc.features || []) {
    if (f.geometry?.type !== "LineString") continue;
    const coords = f.geometry.coordinates || [];
    // Need at least 2 positions for a segment (Draw keeps a trailing vertex while drawing).
    if (coords.length < 2) continue;
    totalKm += length(f, { units: "kilometers" });
  }
  return totalKm * 1000;
}

/**
 * @param {import('mapbox-gl').Map} map
 * @param {{ onDistanceChange?: (label: string) => void, onActiveChange?: (active: boolean) => void }} [options]
 */
export function createMeasurementController(map, options = {}) {
  const { onDistanceChange = () => {}, onActiveChange = () => {} } = options;
  /** @type {InstanceType<typeof MapboxDraw> | null} */
  let draw = null;
  let active = false;

  function notifyDistance() {
    onDistanceChange(formatDistanceMeters(totalLineLengthMeters(draw)));
  }

  function onDrawCreate() {
    notifyDistance();
    if (active && draw) {
      try {
        draw.changeMode("draw_line_string");
      } catch (_) {}
    }
  }

  function onDrawUpdateOrDelete() {
    notifyDistance();
  }

  /** Fires while drawing (vertex moves with cursor), so distance updates before the line is finished. */
  function onDrawRender() {
    notifyDistance();
  }

  function ensureDraw() {
    if (draw) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const attach = () => {
        try {
          draw = new MapboxDraw({ displayControlsDefault: false });
          map.addControl(draw, "top-left");
          map.on("draw.create", onDrawCreate);
          map.on("draw.update", onDrawUpdateOrDelete);
          map.on("draw.delete", onDrawUpdateOrDelete);
          map.on("draw.render", onDrawRender);
          notifyDistance();
          resolve();
        } catch (e) {
          reject(e);
        }
      };
      if (map.isStyleLoaded && map.isStyleLoaded()) attach();
      else map.once("style.load", attach);
    });
  }

  async function activate() {
    await ensureDraw();
    active = true;
    onActiveChange(true);
    try {
      draw.changeMode("draw_line_string");
    } catch (_) {}
    notifyDistance();
  }

  function deactivate() {
    active = false;
    onActiveChange(false);
    if (!draw) return;
    try {
      draw.changeMode("simple_select");
    } catch (_) {}
  }

  function clear() {
    if (!draw) return;
    try {
      draw.deleteAll();
    } catch (_) {}
    notifyDistance();
    if (active) {
      try {
        draw.changeMode("draw_line_string");
      } catch (_) {}
    }
  }

  function destroy() {
    active = false;
    onActiveChange(false);
    if (draw) {
      map.off("draw.create", onDrawCreate);
      map.off("draw.update", onDrawUpdateOrDelete);
      map.off("draw.delete", onDrawUpdateOrDelete);
      map.off("draw.render", onDrawRender);
      try {
        map.removeControl(draw);
      } catch (_) {}
      draw = null;
    }
  }

  return {
    activate,
    deactivate,
    clear,
    destroy,
    isActive: () => active,
  };
}
